export const RECORDING_STEPS_PER_BEAT = 4;

/** Pattern length needed to cover an entire transport take, including silence. */
export function patternLengthForRecordingTake(startBeat, endBeat, existingLength = 16) {
  const start = Math.max(0, Number(startBeat) || 0);
  const end = Math.max(start, Number(endBeat) || start);
  const requiredSteps = Math.ceil(Math.max(0, end - start) * RECORDING_STEPS_PER_BEAT - 1e-7);
  return Math.max(16, Math.round(Number(existingLength) || 0), requiredSteps);
}

/**
 * The instrument clip on this track that a recording at this beat writes into,
 * or null when it needs a clip of its own. A take records into a clip already
 * sitting at its start: lanes never overlap, so no second clip fits there. A
 * take that starts exactly where a clip ends gets a new clip; a single note
 * outside a take joins the clip it lands in, its end included.
 */
export function recordingTargetClip(clips, trackId, beat, { take = false } = {}) {
  const at = Number(beat);
  if (!Number.isFinite(at)) return null;
  return (Array.isArray(clips) ? clips : []).find((clip) => {
    if (clip?.clipType !== 'pattern' || clip.clipData?.kind !== 'instrument' || clip.trackId !== trackId) return false;
    const end = clip.startBeat + clip.durationBeats;
    return at >= clip.startBeat - 1e-6 && (take ? at < end - 1e-6 : at <= end + 1e-6);
  }) || null;
}

/** Convert absolute transport timing into a note event local to one take clip. */
export function recordedNoteTiming(clipStartBeat, noteStartBeat, noteEndBeat, ppq = 96) {
  const safePpq = Math.max(1, Math.round(Number(ppq) || 96));
  const clipStart = Math.max(0, Number(clipStartBeat) || 0);
  const start = Math.max(clipStart, Number(noteStartBeat) || clipStart);
  const end = Math.max(start + (1 / safePpq), Number(noteEndBeat) || start);
  return {
    startTick: Math.max(0, Math.round((start - clipStart) * safePpq)),
    durationTicks: Math.max(1, Math.round((end - start) * safePpq))
  };
}

/**
 * Merge captured note events into a clip's data in one pass.
 *
 * Recording used to commit each note with its own React state update, which
 * re-rendered the whole audio provider per keystroke. That main-thread load
 * delayed the lookahead scheduler — which runs on a timer — and late scheduler
 * ticks are audible as drag and stutter. Buffering notes and merging them
 * together keeps the render count flat no matter how fast the performance is.
 */
export function appendRecordedNotes(clipData, events) {
  if (!clipData) return null;
  const incoming = Array.isArray(events) ? events.filter(Boolean) : [];
  if (incoming.length === 0) return clipData;
  const ppq = Number(clipData.ppq) || 96;
  const noteEvents = [...(clipData.noteEvents || []), ...incoming];
  const ticksPerStep = ppq / RECORDING_STEPS_PER_BEAT;
  const furthestTick = incoming.reduce((furthest, event) => Math.max(
    furthest,
    (Number(event.startTick) || 0) + (Number(event.durationTicks) || 0)
  ), 0);
  const requiredSteps = Math.max(
    16,
    Number(clipData.patternLength) || 16,
    Math.ceil(furthestTick / Math.max(1, ticksPerStep))
  );
  return {
    ...clipData,
    noteEvents,
    patternLength: requiredSteps,
    rowViewModes: { ...(clipData.rowViewModes || {}), 0: 'piano' }
  };
}
